import { toExcelAmount, toExcelDate, toExcelQuantity } from 'components/xlsx/excelCells';
import { ClassifierData } from 'types/classifiers';
import { ExportMapperOptions } from 'types/exports';
import { TimecardListExportItemDto } from 'types/timecards';
import { getClassifierText, t } from 'utils/getTranslations';
import { timecardSecondsToHours } from '../utils/timecardUtils';

/**
 * Compact ("Menetluskulude raport") variant of the timecards export.
 *
 * Keeps only the 14 columns Eversheds asked for. The amount and time columns are numeric cells the
 * export config sums in a bold total row; an absent number is a blank cell rather than 0.
 *
 * Rows come out chronologically (oldest first), the reverse of the list's newest-first default.
 * The sort is stable, so the backend's status/creation order within a single day is preserved.
 */
export const mapTimecardsToCompactFormat = (
    timecards: TimecardListExportItemDto[],
    classifiers: Record<string, ClassifierData>,
    options?: ExportMapperOptions
) => {
    const isTwoDecimalEnabled = options?.isTwoDecimalEnabled ?? false;

    const chronological = timecards?.toSorted((a, b) => a.timecardDate.localeCompare(b.timecardDate));

    return chronological?.map((timecard) => ({
        [t('timecards.export.connected-invoice')]: timecard.invoice?.invoiceNumber ?? '',
        [t('timecards.export.timecard-status')]: getClassifierText(
            classifiers['TIMECARD_STATUS_TYPE'],
            timecard.timecardStatusTypeCode
        ),
        [t('timecards.export.timecard-date')]: toExcelDate(timecard.timecardDate),
        [t('timecards.export.timecard-author')]: timecard.employee.name,
        [t('timecards.export.timecard-invoiced-author')]: timecard.invoiceTimecardDetails?.employee?.name ?? '',
        [t('timecards.export.details-of-work')]: timecard.description,
        [t('timecards.export.invoiced-details-of-work')]: timecard.invoiceTimecardDetails?.description ?? '',
        [t('timecards.export.billed-time')]: toExcelQuantity(
            timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEnabled)
        ),
        [t('timecards.export.invoiced-billed-time')]: toExcelQuantity(
            timecard.isNonBillable ? 0 : timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEnabled)
        ),
        [t('timecards.export.hourly-rate')]: toExcelAmount(timecard.invoiceTimecardDetails?.hourlyPrice),
        [t('timecards.export.price-list-currency')]: timecard.invoiceTimecardDetails?.originalCurrencyTypeCode ?? '',
        [t('timecards.export.timecard-amount-in-price-list-currency')]: toExcelAmount(
            timecard.amountInOriginalCurrency
        ),
        [t('timecards.export.timecard-invoiced-amount-in-invoice-currency')]: toExcelAmount(
            timecard.isNonBillable ? 0 : timecard.invoiceTimecardDetails?.amountInPaymentCurrency
        ),
        [t('timecards.export.invoice-currency')]: timecard.invoiceTimecardDetails?.paymentCurrencyTypeCode ?? '',
    }));
};
